import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';

const {
  FiShield,
  FiCopyright,
  FiInfo,
  FiExternalLink,
  FiHelpCircle,
  FiMap
} = FiIcons;

const Footer = () => {
  const { isDarkMode } = useTheme();

  return (
    <motion.footer
      className={`mt-12 rounded-custom p-8 shadow-custom-xl border transition-colors duration-300 ${isDarkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-600'}`}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <div className="text-center space-y-6">
        <motion.div
          className="flex items-center justify-center gap-3 mb-6"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-indigo-900/30' : 'bg-indigo-100'}`}>
            <SafeIcon icon={FiShield} className="text-2xl text-indigo-600" />
          </div>
          <div>
            <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              Videocorso "Appointment Setter"
            </h3>
            <p className="text-sm text-indigo-600 font-semibold">
              © 2026 – Dynamic-Web Srls
            </p>
          </div>
        </motion.div>

        <motion.div
          className={`p-6 rounded-xl border-l-4 border-indigo-500 ${isDarkMode ? 'bg-gray-900/50' : 'bg-indigo-50'}`}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex items-start gap-3 mb-4">
            <SafeIcon
              icon={FiCopyright}
              className="text-indigo-600 text-xl mt-1 flex-shrink-0"
            />
            <div className="text-left">
              <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Informazioni Legali e Copyright
              </h4>
              <p className="text-sm leading-relaxed mb-3">
                <strong>P.IVA:</strong> 03034040356
              </p>
              <p className="text-sm leading-relaxed">
                Tutti i diritti riservati ai sensi della normativa vigente in materia di diritto d'autore (L. 633/1941 e successive modifiche).
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`p-6 rounded-xl border-l-4 border-red-500 ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}`}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex items-start gap-3">
            <SafeIcon
              icon={FiInfo}
              className="text-red-600 text-xl mt-1 flex-shrink-0"
            />
            <div className="text-left">
              <h4 className="font-semibold mb-2 text-red-600">
                Divieti e Sanzioni
              </h4>
              <p className="text-sm leading-relaxed">
                È severamente vietata la riproduzione, diffusione, vendita, noleggio, prestito, pubblica esecuzione, comunicazione al pubblico o condivisione, anche parziale, del presente videocorso senza espressa autorizzazione scritta di Dynamic-Web Srls. Ogni violazione sarà perseguita nelle sedi civili e penali competenti.
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`p-6 rounded-xl border-l-4 border-blue-500 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-start gap-3">
            <SafeIcon
              icon={FiHelpCircle}
              className="text-blue-600 text-xl mt-1 flex-shrink-0"
            />
            <div className="text-left">
              <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Link Utili
              </h4>
              <div className="flex flex-wrap gap-4">
                <motion.a
                  href="https://faqsetter.dynamic-web.space"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${isDarkMode ? 'bg-blue-600/20 text-blue-400 hover:bg-blue-600/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiHelpCircle} className="text-lg" />
                  FAQ
                  <SafeIcon icon={FiExternalLink} className="text-sm" />
                </motion.a>

                <motion.a
                  href="https://setter-roadmap.dynamic-web.space/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${isDarkMode ? 'bg-green-600/20 text-green-400 hover:bg-green-600/30' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <SafeIcon icon={FiMap} className="text-lg" />
                  Roadmap
                  <SafeIcon icon={FiExternalLink} className="text-sm" />
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`text-center pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <p className="text-sm font-medium">
            <strong>Ragione Sociale:</strong> DYNAMIC-WEB SRLS
          </p>
          <p className="text-xs mt-1 opacity-75">
            Società a Responsabilità Limitata Semplificata
          </p>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;